import { BoxType, PAGE_FILL_BOX_ID, isFrameOrTextBox, type CustomTemplate, type CustomTemplateBox } from './custom-template';

/**
 * Доля холста, с которой слой считается покрывающим страницу: подложки, текстуры, заливки. Одно число для
 * парсера PSD (`layer-to-box.mapper`) и для правила заглушки ниже — иначе один и тот же прямоугольник парсер
 * считал бы подложкой, а извлечение фона — слотом.
 */
export const BACKGROUND_COVERAGE_THRESHOLD = 0.9;

/** Размер холста разворота в пикселях модели: тот же, в котором лежат `rect` боксов. */
export type BackgroundCanvas = Pick<CustomTemplate, 'originalWidth' | 'originalHeight'>;

/** Предикат читает коробку структурно, и союз `CustomTemplateBox` под строгим tsconfig к его форме не сводится. */
type FrameProbe = Parameters<typeof isFrameOrTextBox>[0];

type PlaceholderProbe = {
  type?: string;
  rect?: readonly number[];
  params?: {
    origination?: { kind?: string };
    fill?: { kind?: string };
    stroke?: { width?: number };
    pixelData?: unknown;
    url?: unknown;
    fileId?: unknown;
  };
};

/** Обводка тоньше этой доли меньшей стороны — волосяная линия слота, а не декоративная рамка. */
const HAIRLINE_STROKE_RATIO = 0.02;

/** Тайлы, которыми парсер запёк тень/обводку вокруг слота: `<id слота>-frame-below-N` / `-frame-above-N`. */
const FRAME_TILE = /^(.+)-frame-(below|above)-\d+$/;

/** Прямоугольник во весь холст — заливка страницы из PSD, а не слот: размер решает, положение нет (как у парсера). */
function coversCanvas(rect: readonly number[] | undefined, canvas: BackgroundCanvas | undefined): boolean {
  if (!canvas || !rect) return false;
  return (
    rect[2] >= canvas.originalWidth * BACKGROUND_COVERAGE_THRESHOLD &&
    rect[3] >= canvas.originalHeight * BACKGROUND_COVERAGE_THRESHOLD
  );
}

/**
 * Заглушка под фото: плоский залитый прямоугольник без картинки; волосяная обводка (PSD рисует ею край
 * слота) заглушку декором не делает — жирная обводка уже фигура-рамка, она остаётся. Прямоугольник во
 * весь холст (белый или цветной лист под всем разворотом) — заливка страницы, она остаётся: цвет слот
 * от заливки не отличает (бывают и тёмно-зелёные слоты), размер — отличает.
 */
export function isPhotoPlaceholderDecor(box: CustomTemplateBox, canvas?: BackgroundCanvas): boolean {
  // Заливка страницы («Цвет фона») формой совпадает с заглушкой, но она сама и есть фон — остаётся.
  if (box.id === PAGE_FILL_BOX_ID) return false;
  const probe = box as unknown as PlaceholderProbe;
  if (probe.type !== BoxType.VECTOR) return false;
  if (coversCanvas(probe.rect, canvas)) return false;
  const { params } = probe;
  if (params?.origination?.kind !== 'rectangle' || params.fill?.kind !== 'solid') return false;
  if (params.pixelData !== undefined || params.url !== undefined || params.fileId !== undefined) return false;
  if (params.stroke === undefined) return true;
  const side = Math.min(probe.rect?.[2] ?? 0, probe.rect?.[3] ?? 0);
  return (params.stroke.width ?? 0) <= side * HAIRLINE_STROKE_RATIO;
}

/**
 * Обрамление вокруг слота: запечённый тайл рамки, чей хозяин — заглушка, рамка или плашка под текстом (уже
 * снята с фона, среди соседей его нет). Тайл декоративной фигуры с тенью остаётся вместе с фигурой.
 */
export function isFrameOrnamentAround(
  box: CustomTemplateBox,
  others: readonly CustomTemplateBox[],
  canvas?: BackgroundCanvas,
  textRects: readonly (readonly number[])[] = [],
): boolean {
  if (box.type !== BoxType.OBJECT) return false;
  const ownerId = FRAME_TILE.exec(box.id)?.[1];
  if (ownerId === undefined) return false;
  const owner = others.find(b => b.id === ownerId);
  return (
    owner === undefined ||
    isFrameOrTextBox(owner as unknown as FrameProbe) ||
    isPhotoPlaceholderDecor(owner, canvas) ||
    isTextPlate(owner, textRects, canvas)
  );
}

/** Плашка крупнее надписи больше чем в 10 раз по площади — уже иллюстрация с подписью внутри (автобус с «423»). */
const TEXT_PLATE_MAX_AREA_RATIO = 10;

function textRectsOf(boxes: readonly CustomTemplateBox[], into: (readonly number[])[] = []): (readonly number[])[] {
  for (const box of boxes) {
    if (box.type === BoxType.TEXT && box.rect) into.push(box.rect);
    if (box.type === BoxType.GROUP) textRectsOf(box.boxes, into);
  }

  return into;
}

/**
 * Плашка под текстом (лента «1-Б», подложка подписи): бокс не во весь холст, чья рамка целиком вмещает рамку
 * текста и крупнее её по площади не больше чем в `TEXT_PLATE_MAX_AREA_RATIO` раз. Уходит вместе с текстом.
 * Поворот не учитывается — сравниваются `rect` как есть. Без размера холста правило молчит: полноэкранную
 * текстуру с крупной надписью от плашки тогда не отличить.
 */
function isTextPlate(box: CustomTemplateBox, textRects: readonly (readonly number[])[], canvas?: BackgroundCanvas): boolean {
  if (!canvas || !(canvas.originalWidth > 0) || !(canvas.originalHeight > 0)) return false;
  if (box.type === BoxType.TEXT || box.type === BoxType.BACKGROUND || box.type === BoxType.GROUP) return false;
  const rect = box.rect;
  if (!rect || coversCanvas(rect, canvas)) return false;
  const [x, y, w, h] = rect;
  return textRects.some(
    ([tx, ty, tw, th]) =>
      tx >= x && ty >= y && tx + tw <= x + w && ty + th <= y + h && w * h <= tw * th * TEXT_PLATE_MAX_AREA_RATIO,
  );
}

type ClipperProbe = { type?: string; params?: { compoundClipBakeRecipe?: { leafBoxIds?: readonly string[] } } };

/**
 * Клипперы, привязанные к любому из `ids`: OBJECT, чей рецепт запечки (`compoundClipBakeRecipe.leafBoxIds`)
 * называет хоть один из них. Клиппер уходит вместе с базой (FKS-12999): без буквы или рамки текстура-маска
 * показала бы себя голым прямоугольником. Обход по всему дереву, группы включительно.
 */
export function clipCompanionIdsOf(boxes: readonly CustomTemplateBox[], ids: readonly string[]): string[] {
  const wanted = new Set(ids);
  const out: string[] = [];
  const walk = (list: readonly CustomTemplateBox[]): void => {
    for (const box of list) {
      const probe = box as unknown as ClipperProbe;
      if (probe.type === BoxType.OBJECT && probe.params?.compoundClipBakeRecipe?.leafBoxIds?.some(id => wanted.has(id))) {
        out.push(box.id);
      }

      if (box.type === BoxType.GROUP) walk(box.boxes);
    }
  };

  walk(boxes);
  return out;
}

/**
 * Основа клиппера — все листы его рецепта запечки (`compoundClipBakeRecipe.leafBoxIds`) у OBJECT; у прочих основы нет.
 * Клиппер рисуется внутри объединения листов, поэтому слой разворота судит его по всем листам (ADR 0045 п.3).
 */
export function clipBaseIdsOf(box: CustomTemplateBox): readonly string[] {
  return box.type === BoxType.OBJECT ? (box.params.compoundClipBakeRecipe?.leafBoxIds ?? []) : [];
}

/**
 * База клиппера — первый лист его рецепта ({@link clipBaseIdsOf}); у прочих базы нет. Якорь единицы обтравки пака:
 * за ним клиппер переносится и встаёт вплотную.
 */
export function clipBaseIdOf(box: CustomTemplateBox): string | undefined {
  return clipBaseIdsOf(box)[0];
}

function collectIds(boxes: readonly CustomTemplateBox[], into: Set<string>): Set<string> {
  for (const box of boxes) {
    into.add(box.id);
    if (box.type === BoxType.GROUP) collectIds(box.boxes, into);
  }

  return into;
}

function withoutIds(boxes: CustomTemplateBox[], gone: ReadonlySet<string>): CustomTemplateBox[] {
  return boxes.flatMap<CustomTemplateBox>(box => {
    if (gone.has(box.id)) return [];
    if (box.type !== BoxType.GROUP) return [box];
    const inner = withoutIds(box.boxes, gone);
    return inner.length === 0 ? [] : [{ ...box, boxes: inner }];
  });
}

function stripFramesAndTexts(
  boxes: CustomTemplateBox[],
  textRects: readonly (readonly number[])[],
  canvas?: BackgroundCanvas,
): CustomTemplateBox[] {
  return boxes.flatMap<CustomTemplateBox>(box => {
    if (isFrameOrTextBox(box as unknown as FrameProbe)) return [];
    if (isPhotoPlaceholderDecor(box, canvas) || isFrameOrnamentAround(box, boxes, canvas, textRects)) return [];
    if (isTextPlate(box, textRects, canvas)) return [];
    if (box.type !== BoxType.GROUP) return [box];
    const inner = stripFramesAndTexts(box.boxes, textRects, canvas);
    return inner.length === 0 ? [] : [{ ...box, boxes: inner }];
  });
}

/**
 * Рамки, тексты с их плашками, заглушки под фото и их обрамления удаляются, а не прячутся: скрытые слои засоряли
 * бы «Слои» у фона. Без канвы правило «во весь холст» не действует — каждый вызывающий держит модель с её размером
 * и передаёт её. Вторым проходом уходят клипперы снятых баз (буквы-текстура над TEXT): без базы маска показала бы
 * текстуру прямоугольником. Достаточно одной снятой базы — то же правило, что у ручного удаления базы в редакторе.
 */
export function toBackgroundBoxes(boxes: CustomTemplateBox[], canvas?: BackgroundCanvas): CustomTemplateBox[] {
  const kept = stripFramesAndTexts(boxes, textRectsOf(boxes), canvas);
  const removed = collectIds(boxes, new Set<string>());
  for (const id of collectIds(kept, new Set<string>())) removed.delete(id);
  if (removed.size === 0) return kept;
  const clippers = [...clipCompanionIdsOf(kept, [...removed]), ...clippedAdjustmentIdsOf(kept, removed)];
  return clippers.length === 0 ? kept : withoutIds(kept, new Set(clippers));
}

/** Коррекция, обтравленная по снятой базе, уходит с ней: оставшись, она ссылается на слой, которого нет, и рендер отказывает. */
function clippedAdjustmentIdsOf(boxes: readonly CustomTemplateBox[], removed: ReadonlySet<string>): string[] {
  return boxes.flatMap(box => {
    if (box.type === BoxType.GROUP) return clippedAdjustmentIdsOf(box.boxes, removed);
    const base = box.type === BoxType.ADJUSTMENT ? box.params?.clipBaseBoxId : undefined;
    return base && removed.has(base) ? [box.id] : [];
  });
}
